import { FieldArray } from "formik";
import { get } from "lodash";
import React from "react";
import { Divider } from "semantic-ui-react";
import { type QuestionBuilderPropsBase, type QuestionFormComponents } from "features/Library/Common/props";
import nameof from "utils/nameof";
import { type IQuestion, type IQuestionBase } from "features/Library/Common/models";
import ObjectUtils from "utils/objectUtils";
import styles from "features/Library/Common/QuestionsForm/Questions/QuestionsBuilder/QuestionWithAnswers/QuestionWithAnswers.module.scss";

export type AnswersQuestionBuilderProps<TQuestion extends IQuestion<TData>, TData> = QuestionBuilderPropsBase<TData> &
  QuestionFormComponents<TQuestion, TData> & {
    onReplaceQuestion: (index: number) => (question: IQuestionBase) => void;
  };

export const QuestionWithAnswers = <TQuestion extends IQuestion<TData>, TData>(
  props: AnswersQuestionBuilderProps<TQuestion, TData>,
) => {
  const {
    question,
    validatedFieldProps,
    index,
    onBlur,
    onChanged,
    disabled,
    onReplaceQuestion,
    propertyNamePrefix,
    onRemove,
  } = props;
  const { QuestionRenderer, QuestionHeader, AnswersBuilder } = props;

  return (
    <>
      <div className={styles.container} aria-label={`Question ${index + 1}`}>
        <QuestionHeader
          onQuestionTypeChanged={onReplaceQuestion(index)}
          question={question as TQuestion}
          validatedFieldProps={validatedFieldProps}
          onBlur={onBlur}
          onChanged={onChanged}
          disabled={disabled}
          index={index}
          propertyNamePrefix={propertyNamePrefix}
          onRemove={onRemove}
        />

        <div className={styles.question}>
          <QuestionRenderer
            disabled={disabled}
            index={index}
            onBlur={onBlur}
            onChanged={onChanged}
            question={question as TQuestion}
            validatedFieldProps={validatedFieldProps}
            propertyNamePrefix={propertyNamePrefix}
          />
        </div>

        <div className={styles.answers}>
          <FieldArray
            validateOnChange={false}
            name={`${propertyNamePrefix}${nameof<IQuestionBase>("answers")}`}
            render={(answerArrayHelpers) => (
              <AnswersBuilder
                question={question as TQuestion}
                questionId={index}
                arrayHelpers={answerArrayHelpers}
                validatedFieldProps={validatedFieldProps}
                onBlur={onBlur}
                onChanged={onChanged}
                disabled={disabled}
                propertyNamePrefix={propertyNamePrefix}
              />
            )}
          />
        </div>
      </div>

      <Divider className={styles.divider} />
    </>
  );
};

/* istanbul ignore next */
const getPropsToCompare = (props: AnswersQuestionBuilderProps<IQuestion<unknown>, unknown>) => {
  const { arrayHelpers, validatedFieldProps, ...rest } = props;

  const errors = get(props.validatedFieldProps.errors, `questions.${props.index}`);
  const touched = get(props.validatedFieldProps.touched, `questions.${props.index}`);

  return { ...rest, errors, touched };
};

/* istanbul ignore next */
export default React.memo(QuestionWithAnswers, (prev, next) => {
  return ObjectUtils.shallowEqual(getPropsToCompare(prev), getPropsToCompare(next));
});
